// docs/138 XO5 — the wonder's blessing. Every hero who beholds an exploration's wonder is offered
// three small blessings (a body blessing, a craft blessing, an enchantment for one piece of gear);
// while one of my heroes has an offer with no choice, this dialog asks: "<wonder> blesses <hero>",
// three cards, choose one. It is mounted with the World-level overlays (App.tsx), so it comes up
// during the exploration and stays until chosen after it. "Later" puts it away until the next
// screen; the offer waits on the server either way. The third card is always the enchantment,
// placed afterwards from the inventory like any wonder's gift, and the card says so.
import { useState } from 'react';
import { Badge, Button, Card, Modal, Portrait } from '../components';
import { useSession } from '../../core/stores/session';
import { useRoute } from '../../core/stores/route';
import { useWorldHeroes } from '../../core/subscriptions/hooks';
import { CLASS_NAMES } from '../../core/sim/adapt';
import { chooseWonderBlessing, useMyOpenBlessings, type WonderBlessingRow } from '../../core/explore/blessing';
import { playSfx } from '../../core/audio';
import './explore.css';

/** The third choice: an enchantment, placed from the inventory (docs/112 gifts). */
export const ENCHANT_NOTE = 'An enchantment: choose it now, then lay it on one piece of your gear from the inventory, as any wonder’s gift.';

/** The dialog for the first open offer of mine (the World overlays and the gallery mount it). */
export function BlessingHost() {
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const screen = useRoute((s) => s.screen);
  const open = useMyOpenBlessings(worldId, me);
  // "Later" puts an offer away until the screen changes
  const [later, setLater] = useState<{ ids: string[]; screen: string }>({ ids: [], screen });
  const putBy = later.screen === screen ? later.ids : [];
  const offer = open.find((o) => !putBy.includes(o.id.toString()));
  const heroes = useWorldHeroes(worldId);
  if (!offer) return null;
  const hero = heroes.find((h) => h.id === offer.heroId);
  return (
    <BlessingDialog key={offer.id.toString()} offer={offer} hero={hero ? { name: hero.name, frame: hero.oryxFrame, classId: CLASS_NAMES[hero.class] ?? 'fighter', id: hero.id, species: hero.species } : undefined}
      more={open.length - 1} onLater={() => setLater({ ids: [...putBy, offer.id.toString()], screen })} />
  );
}

/** One offer as a dialog: the hero, the wonder, three cards. */
export function BlessingDialog({ offer, hero, more = 0, onLater }: {
  offer: WonderBlessingRow; hero?: { name: string; frame: number; classId: string; id?: bigint; species?: number }; more?: number; onLater?: () => void;
}) {
  const [picked, setPicked] = useState<number>();
  const [busy, setBusy] = useState(false);
  const name = hero?.name ?? offer.heroName ?? 'Your hero';
  const wonder = cap(offer.wonder || 'The wonder');
  const choose = async (i: number) => {
    if (busy) return;
    setPicked(i); setBusy(true);
    const r = await chooseWonderBlessing(offer.id, i);
    setBusy(false);
    if (r.ok) playSfx('cine.shimmer'); else setPicked(undefined);
  };
  return (
    <Modal open onClose={() => onLater?.()} dismissible={!!onLater} eyebrow="The wonder's blessing" title={`${wonder} blesses ${name}`} size="wide" testId="blessing-dialog"
      footer={onLater ? <Button variant="ghost" onClick={onLater} disabled={busy} data-testid="blessing-later">Later{more > 0 ? ` (${more} more waiting)` : ''}</Button> : undefined}>
      <div className="fk-blessing" data-offer={offer.id.toString()}>
        <div className="fk-blessing__lead">
          {hero && <Portrait heroId={hero.id} species={hero.species} frame={hero.id === undefined ? undefined : hero.frame} classId={hero.classId} scale={3} glow title={name} />}
          <p>{name} has beheld {offer.wonder || 'the wonder'}. Choose one blessing; it stays with {name} for good. It does not cost a skill point.</p>
        </div>
        <div className="fk-levelup__cards">
          {offer.names.slice(0, 3).map((n, i) => {
            const isPicked = picked === i;
            const enchant = i === 2;
            return (
              <Card key={i} as="button" interactive index={i} selected={isPicked} dim={picked !== undefined && !isPicked} column
                className={`fk-levelup__card fk-blessing__card fk-reveal fk-reveal--flip ${enchant ? 'is-enchant' : ''}`} data-testid={`blessing-card-${i}`} onClick={() => void choose(i)} disabled={busy} aria-pressed={isPicked}>
                <span className="fk-levelup__card-no" aria-hidden="true">{enchant ? 'ENCHANTMENT' : i === 0 ? 'BODY' : 'CRAFT'}</span>
                <span className="fk-levelup__card-title">{n}</span>
                <span className="fk-levelup__card-text">{offer.blurbs[i] ?? ''}</span>
                {enchant && <span className="fk-levelup__card-text fk-blessing__note">{ENCHANT_NOTE}</span>}
                {isPicked && <Badge tone="gold" filled className="fk-levelup__chosen">{busy ? 'choosing…' : 'chosen'}</Badge>}
              </Card>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
